import {DeleteRounded, ExpandMore} from "@mui/icons-material";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Grid,
    IconButton,
    Stack,
    Tooltip,
    Typography
} from "@mui/material";
import { useCallback } from "react";
import {Controller, useFormContext, useWatch} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilState } from "recoil";
import { useQuery } from "@tanstack/react-query";
import {
    AncillarySellType,
    googleAutocompleteType,
    MultilanguageName,
    ReactQueryKey,
    RentalType
} from "../../../../../types";
import {AncillarySubtype, getCountries, useProductQuery, useRatePlanQueryObserver} from "../../../../../api/services";
import { FormNumberInput, FormTextField, FormCheckbox } from "../../../../../components/fields";
import { IFormValues } from "../../../types";
import { editAccordionExpandedState, editPendingBookingRce } from "../../../recoil";
import { useRecoilValue } from "recoil";
import { FormCoupon, FormDiscountOverprice, FormDiscountType, FormFcaBankCardNumber } from "./fields";
import { setDecimalSeparator } from "../../../../../utils";
import GoogleAutocompleteWrapper from "../../../../../../../util/component/GoogleAutocompleteWrapper";

interface AncillaryRendererParams {
    ancillaryCode: string,
    maxUnits: number,
    name: MultilanguageName,
    sellType: AncillarySellType,
    mileageNeeded: boolean,
    ancillaryDescription: string,
    subtype: AncillarySubtype
}

const ANCILLARY_AMOUNT_WIDTH = 200;

const AdditionalDriversField = (params: AncillaryRendererParams) => {
    const { i18n } = useTranslation('booking');

    const isPendingBookingRce = useRecoilValue<boolean | undefined>(editPendingBookingRce);

    return (
        <Stack direction="row" alignItems="end" columnGap={2}>
            <Box
                width={100}
                sx={{
                    '& .MuiFormControl-root': {
                        mt: 0
                    }
                }}
            >
                <FormNumberInput<IFormValues>
                    name={`extraServices.ancillary.${AncillarySubtype.AdditionalDrivers}`}
                    label=""
                    disabled={isPendingBookingRce || ['A', 'Z'].includes(params.sellType)}
                    minValue={0}
                    maxValue={2}
                />
            </Box>
            <Tooltip
                title={params.ancillaryDescription}
                disableInteractive
            >
                <Typography mb={1}>
                    {params.name[i18n.language] ?? params.name.defaultLabel}
                </Typography>
            </Tooltip>
        </Stack>
    )
}

const DeliveryField = (params: AncillaryRendererParams) => {

    const { i18n } = useTranslation('booking');
    const formContext = useFormContext<IFormValues>();
    const isPendingBookingRce = useRecoilValue<boolean | undefined>(editPendingBookingRce);

    return (
        <Box>
            <Stack direction="row" alignItems="end" columnGap={2}>
                <Box
                    width={100}
                    sx={{
                        '& .MuiFormControl-root': {
                            mt: 0
                        }
                    }}
                >
                    <FormNumberInput<IFormValues>
                        name={`extraServices.ancillary.${params.ancillaryCode}`}
                        label=""
                        disabled={isPendingBookingRce || ['A', 'Z'].includes(params.sellType)}
                        minValue={0}
                        maxValue={params.mileageNeeded ? 999 : params.maxUnits}
                        onChange={(value: number) => {
                            if(!value) {
                                formContext.setValue('extraServices.deliveryGoogleSearch', undefined);
                                formContext.setValue('extraServices.deliveryStreetName', undefined);
                                formContext.setValue('extraServices.deliveryStreetNumber', undefined);
                                formContext.setValue('extraServices.deliveryPostalCode', undefined);
                                formContext.setValue('extraServices.deliveryCity', undefined);
                                formContext.setValue('extraServices.deliveryState', '');
                            }
                        }}
                    />
                </Box>
                <Tooltip
                    title={params.ancillaryDescription}
                    disableInteractive
                >
                    <Typography
                        mb={1}
                    >
                        {params.name[i18n.language] ?? params.name.defaultLabel}
                    </Typography>
                </Tooltip>
            </Stack>
        </Box>
    )
}

const CollectionField = (params: AncillaryRendererParams) => {

    const { i18n } = useTranslation('booking');
    const formContext = useFormContext<IFormValues>();
    const isPendingBookingRce = useRecoilValue<boolean | undefined>(editPendingBookingRce);

    return (
        <Box>
            <Stack direction="row" alignItems="end" columnGap={2}>
                <Box
                    width={100}
                    sx={{
                        '& .MuiFormControl-root': {
                            mt: 0
                        }
                    }}
                >
                    <FormNumberInput<IFormValues>
                        name={`extraServices.ancillary.${params.ancillaryCode}`}
                        label=""
                        disabled={isPendingBookingRce || ['A', 'Z'].includes(params.sellType)}
                        minValue={0}
                        maxValue={params.mileageNeeded ? 999 : params.maxUnits}
                        onChange={(value: number) => {
                            if(!value) {
                                formContext.setValue('extraServices.collectionGoogleSearch', undefined);
                                formContext.setValue('extraServices.collectionStreetName', undefined);
                                formContext.setValue('extraServices.collectionStreetNumber', undefined);
                                formContext.setValue('extraServices.collectionPostalCode', undefined);
                                formContext.setValue('extraServices.collectionCity', undefined);
                                formContext.setValue('extraServices.collectionState', '');
                            }
                        }}
                    />
                </Box>
                <Tooltip
                    title={params.ancillaryDescription}
                    disableInteractive
                >
                    <Typography
                        mb={1}
                    >
                        {params.name[i18n.language] ?? params.name.defaultLabel}
                    </Typography>
                </Tooltip>
            </Stack>
        </Box>
    )
}

const UrbanDeliveryField = (params: AncillaryRendererParams) => {

    const { i18n } = useTranslation('booking');
    const formContext = useFormContext<IFormValues>();
    const isPendingBookingRce = useRecoilValue<boolean | undefined>(editPendingBookingRce);

    return (
        <>
            <Tooltip
                title={params.ancillaryDescription}
                disableInteractive
            >
                <span>
                    <FormCheckbox<IFormValues>
                        name={`extraServices.ancillary.${params.ancillaryCode}`}
                        disabled={isPendingBookingRce || ['A', 'Z'].includes(params.sellType)}
                        label={
                            params.name[i18n.language] ?? params.name.defaultLabel
                        }
                        sx={{
                            mt: 1
                        }}
                        onChange={(e,check) => {
                            if (!check) {
                                formContext.setValue('extraServices.deliveryGoogleSearch', undefined);
                                formContext.setValue('extraServices.deliveryStreetName', undefined);
                                formContext.setValue('extraServices.deliveryStreetNumber', undefined);
                                formContext.setValue('extraServices.deliveryPostalCode', undefined);
                                formContext.setValue('extraServices.deliveryCity', undefined);
                                formContext.setValue('extraServices.deliveryState', '');
                            }
                        }}
                    />
                </span>
            </Tooltip>
        </>
    )
}

const UrbanCollectionField = (params: AncillaryRendererParams) => {

    const { i18n } = useTranslation('booking');
    const formContext = useFormContext<IFormValues>();
    const isPendingBookingRce = useRecoilValue<boolean | undefined>(editPendingBookingRce);

    return (
        <>
            <Tooltip
                title={params.ancillaryDescription}
                disableInteractive
            >
                <span>
                    <FormCheckbox<IFormValues>
                        name={`extraServices.ancillary.${params.ancillaryCode}`}
                        disabled={isPendingBookingRce || ['A', 'Z'].includes(params.sellType)}
                        label={
                            params.name[i18n.language] ?? params.name.defaultLabel
                        }
                        sx={{
                            mt: 1
                        }}
                        onChange={(e,check) => {
                            if (!check) {
                                formContext.setValue('extraServices.collectionGoogleSearch', undefined);
                                formContext.setValue('extraServices.collectionStreetName', undefined);
                                formContext.setValue('extraServices.collectionStreetNumber', undefined);
                                formContext.setValue('extraServices.collectionPostalCode', undefined);
                                formContext.setValue('extraServices.collectionCity', undefined);
                                formContext.setValue('extraServices.collectionState', '');
                            }
                        }}
                    />
                </span>
            </Tooltip>
        </>
    )
}

const ExtraServices = () => {

    const isPendingBookingRce = useRecoilValue<boolean | undefined>(editPendingBookingRce);

    const { i18n, t } = useTranslation('booking');
    const formContext = useFormContext<IFormValues>();

    const [extraServicesExpanded, setExtraServicesExpanded] = useRecoilState(editAccordionExpandedState('extraServices'));

    const discountType = useWatch<IFormValues, 'extraServices.discountType'>({
        name: 'extraServices.discountType'
    });
    const rentalType = useWatch<IFormValues, 'basicDetails.rentalType'>({
        name: 'basicDetails.rentalType'
    });
    const tenant = useWatch({ control: formContext.control, name: "basicDetails.tenant" });
    const dealer = useWatch({ control: formContext.control, name: "basicDetails.dealer" });

    const productQuery = useProductQuery(
        {
            segment: rentalType,
            businessContinuityInsToolMode: false,
            tenant: tenant as string,
            dealer: dealer as string
        },
        {
            refetchOnMount: false
        }
    );
    const ratePlanQuery = useRatePlanQueryObserver({tenant: tenant as string, dealer: dealer as string});

    const countriesQuery = useQuery({
        queryKey: [ReactQueryKey.Countries],
        queryFn: getCountries,
        refetchOnMount: false
    });

    const googleInputPropsCollectionField = {
        onPlaceSelected: (place: any) => selectedCollectionAddress(place),
        options: {
            fields: ['address_components'],
            types: ['address']
        }
    }

    const googleInputPropsDeliveryField = {
        onPlaceSelected: (place: any) => selectedDeliveryAddress(place),
        options: {
            fields: ['address_components'],
            types: ['address']
        }
    }

    // Reset value collection address field
    const resetCollectionAddress = () => {
        formContext.setValue('extraServices.collectionGoogleSearch', '');
        formContext.setValue('extraServices.collectionStreetName', '');
        formContext.setValue('extraServices.collectionStreetNumber', '');
        formContext.setValue('extraServices.collectionPostalCode', '');
        formContext.setValue('extraServices.collectionCity', '');
        formContext.setValue('extraServices.collectionState', '');
    }

    // Selected collection address and set value form
    const selectedCollectionAddress = useCallback((result : any) => {
        if(!result){
            return;
        }

        const country = result?.address_components?.find((elem: any) => elem.types.includes(googleAutocompleteType.COUNTRY));
        const locality = result?.address_components?.find((elem: any) => elem?.types.includes(googleAutocompleteType.LOCALITY));
        const streetName = result?.address_components?.find((elem: any) => elem.types.includes(googleAutocompleteType.ROUTE));
        const streetNumber = result?.address_components?.find((elem: any) => elem.types.includes(googleAutocompleteType.STREET_NUMBER));
        const postalCode = result?.address_components?.find((elem: any) => elem.types.includes(googleAutocompleteType.POSTAL_CODE));

        // set value form for billing data
        formContext.setValue('extraServices.collectionStreetNumber', !!streetNumber ? streetNumber.short_name : '');
        formContext.setValue('extraServices.collectionState',  country?.short_name ?? '');
        formContext.setValue('extraServices.collectionCity', locality?.short_name ?? '');
        formContext.setValue('extraServices.collectionStreetName',  streetName?.short_name ?? '');
        formContext.setValue('extraServices.collectionPostalCode', postalCode?.short_name ?? '');
        if(!!streetName) {
            formContext.setValue('extraServices.collectionGoogleSearch', streetName.short_name);
        }
    }, []);

    // Reset value delivery address field
    const resetDeliveryAddress = () => {
        formContext.setValue('extraServices.deliveryGoogleSearch', '');
        formContext.setValue('extraServices.deliveryStreetName', '');
        formContext.setValue('extraServices.deliveryStreetNumber', '');
        formContext.setValue('extraServices.deliveryPostalCode', '');
        formContext.setValue('extraServices.deliveryCity', '');
        formContext.setValue('extraServices.deliveryState', '');
    }

    // Selected delivery address and set value form
    const selectedDeliveryAddress = useCallback((result : any) => {
        if(!result){
            return;
        }

        const country = result?.address_components?.find((elem: any) => elem.types.includes(googleAutocompleteType.COUNTRY));
        const locality = result?.address_components?.find((elem: any) => elem?.types.includes(googleAutocompleteType.LOCALITY));
        const streetName = result?.address_components?.find((elem: any) => elem.types.includes(googleAutocompleteType.ROUTE));
        const streetNumber = result?.address_components?.find((elem: any) => elem.types.includes(googleAutocompleteType.STREET_NUMBER));
        const postalCode = result?.address_components?.find((elem: any) => elem.types.includes(googleAutocompleteType.POSTAL_CODE));

        // set value form for delivery data
        formContext.setValue('extraServices.deliveryStreetNumber', !!streetNumber ? streetNumber.short_name : '');
        formContext.setValue('extraServices.deliveryState',  country?.short_name ?? '');
        formContext.setValue('extraServices.deliveryCity', locality?.short_name ?? '');
        formContext.setValue('extraServices.deliveryStreetName',  streetName?.short_name ?? '');
        formContext.setValue('extraServices.deliveryPostalCode', postalCode?.short_name ?? '');
        if(!!streetName) {
            formContext.setValue('extraServices.deliveryGoogleSearch', streetName.short_name);
        }
    }, []);

    const extraServices = useWatch<IFormValues>({
        name: `extraServices.ancillary`
    }) as number | undefined;

    const checkFormValue = useCallback((a: any) => {
        return !!formContext.getValues(`extraServices.ancillary.${a}`)
    }, [extraServices]);

    const renderAncillary = useCallback((params: AncillaryRendererParams) => {
        if (params.subtype === AncillarySubtype.AdditionalDrivers) {
            return (
                <AdditionalDriversField
                    {...params}
                />
            )
        }
        if (params.subtype === AncillarySubtype.Delivery) {
            return (
                <DeliveryField
                    {...params}
                />
            )
        }
        if (params.subtype === AncillarySubtype.Collection) {
            return (
                <CollectionField
                    {...params}
                />
            )
        }
        if (params.subtype === AncillarySubtype.UrbanCollection) {
            return (
                <UrbanCollectionField
                    {...params}
                />
            )
        }
        if (params.subtype === AncillarySubtype.UrbanDelivery) {
            return (
                <UrbanDeliveryField
                    {...params}
                />
            )
        }
        if (params.maxUnits > 1 || params.mileageNeeded) {
            return (
                <Stack direction="row" alignItems="end" columnGap={2}>
                    <Box
                        width={100}
                        sx={{
                            '& .MuiFormControl-root': {
                                mt: 0
                            }
                        }}
                    >
                        <FormNumberInput<IFormValues>
                            name={`extraServices.ancillary.${params.ancillaryCode}`}
                            label=""
                            disabled={isPendingBookingRce || ['A', 'Z'].includes(params.sellType)}
                            minValue={0}
                            maxValue={params.mileageNeeded ? 999 : params.maxUnits}
                        />
                    </Box>
                    <Tooltip
                        title={params.ancillaryDescription}
                        disableInteractive
                    >
                        <Typography
                            mb={1}
                        >
                            {params.name[i18n.language] ?? params.name.defaultLabel}
                        </Typography>
                    </Tooltip>
                </Stack>
            )
        }
        else {
            return (
                <Tooltip
                    title={params.ancillaryDescription}
                    disableInteractive
                >
                    <span>
                        <FormCheckbox<IFormValues>
                            name={`extraServices.ancillary.${params.ancillaryCode}`}
                            disabled={isPendingBookingRce || ['A', 'Z'].includes(params.sellType)}
                            label={
                                params.name[i18n.language] ?? params.name.defaultLabel
                            }
                            sx={{
                                mt: 1
                            }}
                        />
                    </span>
                </Tooltip>
            )
        }
    }, []);

    return (
        <Accordion expanded={extraServicesExpanded} onChange={(e, expanded) => setExtraServicesExpanded(expanded)}>
            <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h5">
                    {t('label.furtherDetails')}
                </Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Box pb={1} sx={{ overflowX: 'auto' }}>
                    <Stack mt={2}>
                        <Stack direction="row" pb={1}>
                            <Typography
                                variant="h6"
                                flex={1}
                            >
                                {t('label.ancillary')}
                            </Typography>
                            {productQuery.data?.products.map(p => (
                                <Typography
                                    key={p.code}
                                    variant="h6"
                                    textAlign="end"
                                    minWidth={ANCILLARY_AMOUNT_WIDTH}
                                >
                                    {p.label}
                                </Typography>
                            ))}
                        </Stack>
                        {ratePlanQuery.data?.ancillaries.map((a,i) => (
                            <>
                                <Stack key={a.ancillaryCode} direction="row" alignItems="center">
                                <Box
                                    flex={1}
                                >
                                    {renderAncillary({
                                        ...a,
                                        sellType: a.ancillaryDetails[0].sellType
                                    })}
                                </Box>
                                {productQuery.data?.products.map(p => (
                                    <Typography
                                        key={p.code}
                                        variant="body2"
                                        minWidth={ANCILLARY_AMOUNT_WIDTH}
                                        textAlign="end"
                                    >
                                        { a.ancillaryDetails.find(v => v.productCode === p.code) ?
                                            `${setDecimalSeparator(((a.ancillaryDetails.find(v => v.productCode === p.code)?.amount ?? 0) / 100).toFixed(2), i18n.language)} ${a.currency}${a.mileageNeeded ? '/km' : a.maxUnits > 1 ? '/u' : ''}`
                                            : ' - '
                                        }
                                    </Typography>
                                ))}
                            </Stack>
                                <Stack key={i} direction="column">
                                    <>
                                        { checkFormValue(a.ancillaryCode) && (a.subtype === AncillarySubtype.Collection || a.subtype === AncillarySubtype.UrbanCollection) ?
                                            <>
                                                {/* api google for collection address*/}
                                                <Grid container xs={12}>
                                                    <Grid item xs={11} sm={11}>
                                                        <Controller
                                                            name="extraServices.collectionGoogleSearch"
                                                            control={formContext.control}
                                                            render={({field: {onChange, value}}: any) => (

                                                                <GoogleAutocompleteWrapper
                                                                    googleInputProps={googleInputPropsCollectionField}
                                                                    inputProps={
                                                                        {
                                                                            fullWidth: true,
                                                                            label: t('label.googleFilterAddress'),
                                                                            id: 'collectionGoogleSearch',
                                                                            name: 'extraServices.collectionGoogleSearch',
                                                                            type: 'text',
                                                                            value: value || '',
                                                                            onChange: (e) => {
                                                                                onChange(e.target.value)
                                                                                if (!e.target.value) {
                                                                                    resetCollectionAddress()
                                                                                }
                                                                            },
                                                                        }
                                                                    }
                                                                />
                                                            )}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={1} sm={1} alignItems="center"
                                                          sx={{textAlign: 'center', marginTop: 'auto'}}>
                                                        <Tooltip arrow placement={"top"} title={t("label.reset")}>
                                                            <IconButton onClick={resetCollectionAddress}>
                                                                <DeleteRounded fontSize="small"/>
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Grid>
                                                </Grid>
                                                <Grid container xs={12} spacing={1}>
                                                    <Grid item xs={12} sm={4} >
                                                        <FormTextField<IFormValues>
                                                            name="extraServices.collectionStreetName"
                                                            shouldUnregister
                                                            defaultValue=""
                                                            label={t('label.collectionStreetName') + ' *'}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={4} >
                                                        <FormTextField<IFormValues>
                                                            name="extraServices.collectionStreetNumber"
                                                            shouldUnregister
                                                            defaultValue=""
                                                            label={t('label.collectionStreetNumber') + ' *'}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={4} >
                                                        <FormTextField<IFormValues>
                                                            name="extraServices.collectionPostalCode"
                                                            shouldUnregister
                                                            defaultValue=""
                                                            label={t('label.collectionPostalCode') + ' *'}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={4} >
                                                        <FormTextField<IFormValues>
                                                            name="extraServices.collectionCity"
                                                            shouldUnregister
                                                            defaultValue=""
                                                            label={t('label.collectionCity') + ' *'}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={4} >
                                                        <FormTextField<IFormValues>
                                                            name="extraServices.collectionState"
                                                            shouldUnregister
                                                            defaultValue=""
                                                            label={t('label.collectionState') + ' *'}
                                                        />
                                                        {/* todo sostituire risoluzione bug con clona e draft si perde il valore del control*/}
                                                        {/*                                        <FormAutocomplete<IFormValues>
                                                    name="extraServices.collectionState"
                                                    label={t('label.collectionState') + ' *'}
                                                    options={countriesQuery?.data ?? []}
                                                    loading={countriesQuery.isLoading}
                                                    isOptionEqualToValue={(option, value) => option === value}
                                                    disabled={step === InsertBookingStep.CUSTOMER_DETAILS}
                                                    />*/}
                                                    </Grid>
                                                </Grid>
                                            </>:
                                            undefined }
                                        { checkFormValue(a.ancillaryCode) && (a.subtype === AncillarySubtype.Delivery || a.subtype === AncillarySubtype.UrbanDelivery)
                                          ?
                                            <>
                                                {/* api google for collection address*/}
                                                <Grid container xs={12}>
                                                    <Grid item xs={11} sm={11}>
                                                        <Controller
                                                            name="extraServices.deliveryGoogleSearch"
                                                            control={formContext.control}
                                                            render={({field: {onChange, value}}: any) => (
                                                                <GoogleAutocompleteWrapper
                                                                    googleInputProps={googleInputPropsDeliveryField}
                                                                    inputProps={
                                                                        {
                                                                            fullWidth: true,
                                                                            label: t('label.googleFilterAddress'),
                                                                            id: 'deliveryGoogleSearch',
                                                                            name: 'extraServices.deliveryGoogleSearch',
                                                                            type: 'text',
                                                                            value: value || '',
                                                                            onChange: (e) => {
                                                                                onChange(e.target.value)
                                                                                if (!e.target.value) {
                                                                                    resetDeliveryAddress()
                                                                                }
                                                                            },
                                                                        }
                                                                    }
                                                                />
                                                            )}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={1} sm={1} alignItems="center"
                                                          sx={{textAlign: 'center', marginTop: 'auto'}}>
                                                        <Tooltip arrow placement={"top"} title={t("label.reset")}>
                                                            <IconButton onClick={resetDeliveryAddress}>
                                                                <DeleteRounded fontSize="small"/>
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Grid>
                                                </Grid>
                                                <Grid container xs={12} spacing={1}>
                                                    <Grid item xs={12} sm={4} >
                                                        <FormTextField<IFormValues>
                                                            name="extraServices.deliveryStreetName"
                                                            shouldUnregister
                                                            defaultValue=""
                                                            label={t('label.deliveryStreetName') + ' *'}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={4} >
                                                        <FormTextField<IFormValues>
                                                            name="extraServices.deliveryStreetNumber"
                                                            shouldUnregister
                                                            defaultValue=""
                                                            label={t('label.deliveryStreetNumber') + ' *'}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={4} >
                                                        <FormTextField<IFormValues>
                                                            name="extraServices.deliveryPostalCode"
                                                            shouldUnregister
                                                            defaultValue=""
                                                            label={t('label.deliveryPostalCode') + ' *'}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={4} >
                                                        <FormTextField<IFormValues>
                                                            name="extraServices.deliveryCity"
                                                            shouldUnregister
                                                            defaultValue=""
                                                            label={t('label.deliveryCity') + ' *'}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={4} >
                                                        <FormTextField<IFormValues>
                                                            name="extraServices.deliveryState"
                                                            shouldUnregister
                                                            defaultValue=""
                                                            label={t('label.deliveryState') + ' *'}
                                                        />
                                                        {/* todo sostituire risoluzione bug con clona e draft si perde il valore del control*/}
{/*                                                    <FormAutocomplete<IFormValues>
                                                        name="extraServices.deliveryState"
                                                        label={t('label.deliveryState') + ' *'}
                                                        options={countriesQuery?.data ?? []}
                                                        loading={countriesQuery.isLoading}
                                                        isOptionEqualToValue={(option, value) => option === value}
                                                    />*/}
                                                    </Grid>
                                                </Grid>
                                            </>:
                                            undefined
                                        }
                                    </>
                                </Stack>
                            </>
                        ))}
                    </Stack>
                </Box>
                <Grid container rowSpacing={2} columnSpacing={3}>
                    { rentalType === RentalType.RETAIL &&
                        <>
                            <Grid item xs={12} sm={6}>
                                <FormDiscountType />
                                {discountType === 'promo' ?
                                    <FormCoupon /> :
                                    discountType === 'fca' ?
                                        <FormFcaBankCardNumber /> :
                                        undefined
                                }
                            </Grid>
                            <Grid item xs={12} sm={6} alignSelf="flex-start">
                                <FormDiscountOverprice />
                            </Grid>
                            <Grid item xs={12} >
                                <FormTextField<IFormValues>
                                    name="extraServices.note"
                                    disabled={isPendingBookingRce}
                                    label={t('label.note')}
                                />
                            </Grid>
                        </>
                    }
                    { rentalType === RentalType.RETAIL &&
                        <Grid item xs={12} sm={6}>
                            <FormTextField<IFormValues>
                                name="extraServices.affiliationCode"
                                label={t('label.affiliationCode')}
                                disabled={true}
                            />
                        </Grid>
                    }
                    <Grid item xs={12} sm={6}>
                        <FormTextField<IFormValues>
                            name="extraServices.flightNumber"
                            label={t('label.flightNumber')}
                            disabled={isPendingBookingRce}
                        />
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion >
    );
};

export default ExtraServices;
